import React, { useState } from 'react';
import styles from './styles/App.module.css';
import shared from './styles/shared.module.css';

// Components
import AuthGuard from './components/AuthGuard';
import Dashboard from './components/Dashboard';
import DropZone from './components/DropZone';
import PrioritySelector from './components/PrioritySelector';
import LoadingState from './components/LoadingState';
import BatchProgress from './components/BatchProgress';
import Results from './components/Results';
import BatchResults from './components/BatchResults';
import PricingModal from './components/PricingModal';
import ZoteroImport from './components/ZoteroImport';

// Hooks
import { useEvaluation } from './hooks/useEvaluation';
import { useProfile } from './hooks/useProfile';
import { useUsage } from './hooks/useUsage';
import { useHistory } from './hooks/useHistory';

// Types
import type { Theme, Priority, EvaluationResult } from './types';

// App views — controls what's rendered
type AppView =
  | 'dashboard'
  | 'dropzone'
  | 'zotero'
  | 'priority'
  | 'loading'
  | 'result'
  | 'batch_result'
  | 'selected_paper'
  | 'zotero_result';

export default function App() {
  const [theme, setTheme] = useState<Theme>(() => {
    return (localStorage.getItem('dr_theme') as Theme) || 'scholar';
  });

  const [view, setView] = useState<AppView>('dashboard');
  const [files, setFiles] = useState<File[]>([]);
  const [selectedPaper, setSelectedPaper] = useState<EvaluationResult | null>(null);
  const [showPricing, setShowPricing] = useState(false);

  const { tier } = useProfile();
  const { used, canEvaluate, incrementUsage, refetch: refetchUsage } = useUsage(tier);
  const { result, batchResult, loading, batchProgress, error, evaluate, reset } = useEvaluation();
  const {
    history,
    loading: historyLoading,
    totalTimeSavedMinutes,
    saveEvaluation,
    saveBatch,
    deleteEvaluation,
    refetch: refetchHistory,
  } = useHistory();

  const toggleTheme = () => {
    const next = theme === 'scholar' ? 'alien' : 'scholar';
    setTheme(next);
    localStorage.setItem('dr_theme', next);
    document.documentElement.setAttribute('data-theme', next === 'alien' ? 'alien' : '');
  };

  const handleFiles = (incoming: File[]) => {
    setFiles(incoming);
    setView('priority');
  };

  const handleSubmit = async (priority: Priority) => {
    setView('loading');
    await evaluate(files, priority);
    // Increment usage
    for (let i = 0; i < files.length; i++) {
      await incrementUsage();
    }
    refetchUsage();
  };

  // Save to Supabase after evaluation completes, then refresh history
  React.useEffect(() => {
    if (result && !loading) {
      saveEvaluation(result).then(() => {
        refetchHistory();
      });
      setView('result');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, loading]);

  React.useEffect(() => {
    if (batchResult && !loading) {
      saveBatch(batchResult).then(() => {
        refetchHistory();
      });
      setView('batch_result');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchResult, loading]);

  const handleReEvaluate = async (priority: Priority) => {
    const targetFile = selectedPaper
      ? files.find(f => f.name === selectedPaper.filename) || files[0]
      : files[0];
    if (!targetFile) return;
    setSelectedPaper(null);
    setView('loading');
    await evaluate([targetFile], priority);
  };

  const handleReset = () => {
    setFiles([]);
    setSelectedPaper(null);
    reset();
    setView('dropzone');
  };

  const handleZoteroFiles = (files: File[]) => {
    // Filter to only PDFs for evaluation
    const pdfs = files.filter(f => f.name.toLowerCase().endsWith('.pdf'));
    setFiles(pdfs);
    setView('priority');
  };

  const handleSelectFromHistory = (result: EvaluationResult) => {
    setSelectedPaper(result);
    setView('selected_paper');
  };

  const themeProps = { theme, onToggleTheme: toggleTheme };

  const renderContent = () => {
    // Loading states
    if (loading && files.length > 1 && batchProgress >= 0) {
      return <BatchProgress files={files} current={batchProgress} {...themeProps} />;
    }
    if (loading && files.length <= 1) {
      return <LoadingState filename={files[0]?.name || ''} {...themeProps} />;
    }

    switch (view) {

      case 'zotero':
        return (
          <ZoteroImport
            onZipSelected={handleZoteroFiles}
            onBack={() => setView('dashboard')}
            {...themeProps}
          />
        );

      case 'selected_paper':
        return selectedPaper ? (
          <Results
            result={selectedPaper}
            onReset={handleReset}
            onReEvaluate={handleReEvaluate}
            onBack={() => batchResult ? setView('batch_result') : setView('dashboard')}
            {...themeProps}
          />
        ) : null;

      case 'batch_result':
        return batchResult ? (
          <BatchResults
            batch={batchResult}
            onReset={handleReset}
            onSelectPaper={paper => {
              setSelectedPaper(paper);
              setView('selected_paper');
            }}
            onBack={() => setView('dashboard')}
            {...themeProps}
          />
        ) : null;

      case 'result':
        return result ? (
          <Results
            result={result}
            onReset={handleReset}
            onReEvaluate={handleReEvaluate}
            onBack={() => setView('dashboard')}
            {...themeProps}
          />
        ) : null;

      case 'priority':
        return files.length ? (
          <PrioritySelector
            files={files}
            onSubmit={handleSubmit}
            onBack={() => setView('dropzone')}
            {...themeProps}
          />
        ) : null;

      case 'dropzone':
        return (
          <div className={styles.app}>
            {error && <div className={shared.errorBanner}>{error}</div>}
            <div style={{ position: 'absolute', top: 16, left: 24, zIndex: 10 }}>
              <button
                onClick={() => setView('dashboard')}
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 12,
                  color: 'var(--text-muted)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                ← Dashboard
              </button>
            </div>
            <DropZone
              onFiles={handleFiles}
              tier={tier}
              used={used}
              canEvaluate={canEvaluate}
              onUpgradeClick={() => setShowPricing(true)}
              {...themeProps}
            />
            {showPricing && (
              <PricingModal
                currentTier={tier}
                onClose={() => setShowPricing(false)}
              />
            )}
          </div>
        );

      case 'dashboard':
      default:
        return (
          <div className={styles.app}>
            {error && <div className={shared.errorBanner}>{error}</div>}
            <Dashboard
              history={history}
              historyLoading={historyLoading}
              totalTimeSavedMinutes={totalTimeSavedMinutes}
              tier={tier}
              used={used}
              onNewEvaluation={() => setView('dropzone')}
              onZoteroImport={() => setView('zotero')}
              onSelectResult={handleSelectFromHistory}
              onDeleteEvaluation={deleteEvaluation}
              onUpgradeClick={() => setShowPricing(true)}
              {...themeProps}
            />
            {showPricing && (
              <PricingModal
                currentTier={tier}
                onClose={() => setShowPricing(false)}
              />
            )}
          </div>
        );
    }
  };

  return (
    <AuthGuard {...themeProps}>
      {renderContent()}
    </AuthGuard>
  );
}
